import {
  Box,
  Button,
  CircularProgress,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import { CloudDownload, FileCopyOutlined, Refresh } from "@material-ui/icons";
import { EpocusBillerExportStatus } from "elephant-web/gql";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { copyToClipboard } from "../../_lib/copy-to-clipboard";
import {
  EPOCUS_BORDER,
  EPOCUS_GREEN,
  EPOCUS_PANEL_SCOPED_BORDER,
  EPOCUS_PANEL_SCOPED_HEADER_BG,
  EPOCUS_PRIMARY,
  EPOCUS_RED,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { linkToEpocusBilling } from "../../links";
import {
  type EpocusBillerExportFieldsFragment,
  useCreateEpocusBillerExportMutation,
  useEpocusBillerExportScopeQuery,
  useEpocusBillerExportsQuery,
  useRequestEpocusBillerExportDownloadMutation,
  useRetryEpocusBillerExportMutation,
} from "./__generated__/biller-export-operations.gql";
import {
  BillingCard,
  BillingCardHeader,
  BillingCardTitle,
  BillingHint,
} from "./billing-chrome";
import { formatServiceDay, isIsoDay, toServiceDate } from "./service-day";

const LIVE_STATUS_POLL_MS = 3000;
const DAY_MS = 24 * 60 * 60 * 1000;

const Body = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
});

const RangeRow = styled(Box)({
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
});

const BatchRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  padding: "12px 16px",
  borderTop: `1px solid ${EPOCUS_BORDER}`,
});

const BatchTitle = styled(Typography)({
  fontSize: rem(14),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
});

const BatchMeta = styled(Typography)({
  fontSize: rem(12.5),
  color: EPOCUS_TEXT_SECONDARY,
});

const Actions = styled(Box)({
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: 8,
});

const IncompleteLink = styled(Link)({
  color: EPOCUS_PRIMARY,
  fontWeight: 600,
});

const PasswordPanel = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 6,
  backgroundColor: EPOCUS_PANEL_SCOPED_HEADER_BG,
  border: `1px solid ${EPOCUS_PANEL_SCOPED_BORDER}`,
  borderRadius: 8,
  padding: "10px 12px",
});

const PasswordRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
});

const PasswordText = styled("code")({
  fontSize: rem(15),
  fontWeight: 600,
  letterSpacing: "0.04em",
  color: EPOCUS_TEXT_PRIMARY,
  userSelect: "all",
});

const ZipPassword: FC<{ batchNumber: number; password: string }> = ({
  batchNumber,
  password,
}) => {
  const [copied, setCopied] = useState(false);
  return (
    <PasswordPanel>
      <BatchMeta>ZIP password for batch #{batchNumber}</BatchMeta>
      <PasswordRow>
        <PasswordText>{password}</PasswordText>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileCopyOutlined />}
          onClick={() =>
            copyToClipboard(password).then(
              () => setCopied(true),
              () => setCopied(false),
            )
          }
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </PasswordRow>
      <BillingHint>
        Send it to your billing company separately from the file, never in the
        same email. Windows and macOS open the ZIP when asked for it.
      </BillingHint>
    </PasswordPanel>
  );
};

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

const nextDay = (isoDay: string) =>
  new Date(new Date(toServiceDate(isoDay)).getTime() + DAY_MS).toISOString();

const StatusLabel: FC<{ status: EpocusBillerExportStatus }> = ({ status }) => {
  switch (status) {
    case EpocusBillerExportStatus.Generating:
      return (
        <Box style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <CircularProgress size={14} aria-label="Generating" />
          <BatchMeta>Generating</BatchMeta>
        </Box>
      );
    case EpocusBillerExportStatus.Ready:
      return <BatchMeta style={{ color: EPOCUS_GREEN }}>Ready</BatchMeta>;
    case EpocusBillerExportStatus.Failed:
      return <BatchMeta style={{ color: EPOCUS_RED }}>Failed</BatchMeta>;
    case EpocusBillerExportStatus.Expired:
      return <BatchMeta>Expired, downloading rebuilds it</BatchMeta>;
  }
};

const BatchListRow: FC<{
  batch: EpocusBillerExportFieldsFragment;
  onDownload: (exportId: string) => void;
  onRetry: (exportId: string) => void;
  busy: boolean;
}> = ({ batch, onDownload, onRetry, busy }) => (
  <BatchRow>
    <Box>
      <BatchTitle>
        Batch #{batch.batchNumber} ·{" "}
        {formatServiceDay(batch.createdAt.slice(0, 10))}
      </BatchTitle>
      <BatchMeta>
        {plural(batch.studyCount, "study", "studies")} ·{" "}
        {plural(batch.chargeLineCount, "charge line", "charge lines")}
      </BatchMeta>
    </Box>
    <Actions>
      <StatusLabel status={batch.status} />
      {batch.status === EpocusBillerExportStatus.Failed && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh />}
          disabled={busy}
          onClick={() => onRetry(batch.id)}
        >
          Retry
        </Button>
      )}
      {(batch.status === EpocusBillerExportStatus.Ready ||
        batch.status === EpocusBillerExportStatus.Expired) && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<CloudDownload />}
          disabled={busy}
          onClick={() => onDownload(batch.id)}
        >
          Download
        </Button>
      )}
    </Actions>
  </BatchRow>
);

export const BillerExportCard: FC<{ programId: string }> = ({ programId }) => {
  const [fromDay, setFromDay] = useState("");
  const [toDay, setToDay] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{
    batchNumber: number;
    password: string;
  } | null>(null);

  const range = {
    from: isIsoDay(fromDay) ? toServiceDate(fromDay) : null,
    to: isIsoDay(toDay) ? nextDay(toDay) : null,
  };

  const scope = useEpocusBillerExportScopeQuery({
    variables: { programId, ...range },
    fetchPolicy: "cache-and-network",
  });
  const batches = useEpocusBillerExportsQuery({
    variables: { programId },
    fetchPolicy: "cache-and-network",
  });
  const list = batches.data?.epocusBillerExports ?? [];
  const generating = list.some(
    (batch) => batch.status === EpocusBillerExportStatus.Generating,
  );
  const { startPolling, stopPolling } = batches;
  useEffect(() => {
    if (!generating) return;
    startPolling(LIVE_STATUS_POLL_MS);
    return () => stopPolling();
  }, [generating, startPolling, stopPolling]);

  const refetchAll = () => Promise.all([scope.refetch(), batches.refetch()]);

  const [createExport, { loading: creating }] =
    useCreateEpocusBillerExportMutation({
      onCompleted: async (data) => {
        setNotice(
          data.createEpocusBillerExport
            ? null
            : "Nothing is ready to export right now.",
        );
        await refetchAll();
      },
      onError: () => setNotice("Couldn't create the export. Try again."),
    });
  const [retryExport, { loading: retrying }] =
    useRetryEpocusBillerExportMutation({
      onCompleted: () => batches.refetch(),
      onError: () => setNotice("Couldn't retry the export."),
    });
  const [requestDownload, { loading: downloading }] =
    useRequestEpocusBillerExportDownloadMutation({
      onCompleted: async (data) => {
        const {
          url,
          zipPassword,
          export: batch,
        } = data.requestEpocusBillerExportDownload;
        if (url) {
          setNotice(null);
          setRevealed(
            zipPassword
              ? { batchNumber: batch.batchNumber, password: zipPassword }
              : null,
          );
          window.location.assign(url);
          return;
        }
        setRevealed(null);
        setNotice(
          "This export is being rebuilt from what was sent. Download it again once it shows Ready.",
        );
        await batches.refetch();
      },
      onError: () => setNotice("Couldn't get a download link."),
    });

  const counts = scope.data?.epocusBillerExportScope;
  const readyCount = counts?.readyCount ?? 0;

  return (
    <BillingCard>
      <BillingCardHeader>
        <BillingCardTitle>Biller export</BillingCardTitle>
        <BillingHint>
          A ZIP of PDF reports and a summary CSV for your billing company
        </BillingHint>
      </BillingCardHeader>
      <Body>
        <RangeRow>
          <TextField
            type="date"
            label="From (optional)"
            value={fromDay}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setFromDay(event.target.value)}
          />
          <TextField
            type="date"
            label="To (optional)"
            value={toDay}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            onChange={(event) => setToDay(event.target.value)}
          />
          <Button
            color="primary"
            variant="contained"
            disableElevation
            disabled={readyCount === 0 || creating}
            onClick={() => createExport({ variables: { programId, ...range } })}
          >
            Export ready-to-bill
          </Button>
        </RangeRow>
        {counts && (
          <BillingHint>
            {plural(readyCount, "study is", "studies are")} ready to export
            {counts.amendedCount > 0
              ? `, ${counts.amendedCount} of them amended after export`
              : ""}
            .
            {counts.incompleteCount > 0 && (
              <>
                {" "}
                <IncompleteLink to={linkToEpocusBilling()}>
                  {plural(
                    counts.incompleteCount,
                    "incomplete study was",
                    "incomplete studies were",
                  )}{" "}
                  not exported
                </IncompleteLink>
                .
              </>
            )}
            {counts.heldCount > 0 && (
              <>
                {" "}
                {plural(
                  counts.heldCount,
                  "study is held back by QA review and is",
                  "studies are held back by QA review and are",
                )}{" "}
                listed at the foot of the summary.
              </>
            )}
          </BillingHint>
        )}
        {notice && <BillingHint role="status">{notice}</BillingHint>}
        {revealed && (
          <ZipPassword
            batchNumber={revealed.batchNumber}
            password={revealed.password}
          />
        )}
      </Body>
      {list.map((batch) => (
        <BatchListRow
          key={batch.id}
          batch={batch}
          busy={downloading || retrying}
          onDownload={(exportId) =>
            requestDownload({ variables: { programId, exportId } })
          }
          onRetry={(exportId) =>
            retryExport({ variables: { programId, exportId } })
          }
        />
      ))}
    </BillingCard>
  );
};
